import type { SupabaseClient } from '@supabase/supabase-js'

import { Tables, TablesInsert, TablesUpdate } from '@/supabase/types'

import { createServiceClient } from '@/lib/supabase/service-client'
import type { Database } from '@/supabase/types'
import { isDev } from '@/lib/utils/isDev'
import {
  notifySidebarChatCreated,
  notifySidebarChatUpdated
} from '@/lib/sidebar/sidebar-mutation-events'
import { normalizeSpaceEmojiIcon } from '@/lib/spaces/icon-emoji'
import { getWorkspaceRoleForUser } from '@/lib/workspaces/workspace-access'
import { safeIndexEntity } from '@/lib/knowledge-graph/safe-index'

type ChatRow = Tables<'chats'>
/** Extra list-only field — Space emoji for `{emoji} / {name}` titles (not a DB column). */
export type ChatSpaceIconFields = {
  space_icon?: string | null
}
export type ChatWithSpaceIcon = ChatRow & ChatSpaceIconFields
type ChatSearchMatch = {
  message_id: string
  content: string
}
type ChatSearchRow = {
  chat: ChatWithSpaceIcon
  matches: ChatSearchMatch[]
}
type WorkspaceChatPage = {
  chats: ChatWithSpaceIcon[]
  hasMore: boolean
  nextCursor: string | null
}
type AuthenticatedFetch = typeof fetch

interface BrowserWorkspaceChatReadOptions {
  all?: boolean
  cursor?: string | null
  limit?: number
  offset?: number
  pinned?: boolean
}

interface BrowserWorkspaceChatSearchOptions {
  nameOnly?: boolean
}

interface AccessibleWorkspaceChatPageOptions
  extends BrowserWorkspaceChatReadOptions {
  client?: SupabaseClient<Database>
}

type WorkspaceChatAccessContext = {
  workspaceRole: string | null
}

type ChatMemberAccessRow = {
  chat_id: string
  status: string | null
}

type SpaceChatAccessRow = {
  chat_id: string
  space_id: string
}

type SpaceAccessRow = {
  id: string
  user_id: string
  sharing: string | null
}

type SpaceMemberAccessRow = {
  space_id: string
  role: string | null
}

type WorkspaceChatReadScope = {
  chatIds: string[]
  workspaceRole: string | null
}

const ACTIVE_CHAT_MEMBER_STATUSES = new Set(['accepted', 'active'])
const GROUP_CHAT_MANAGE_ROLES = new Set(['owner', 'admin'])
const WRITEABLE_SPACE_ROLES = new Set(['owner', 'admin', 'editor'])

/** Use service client on server when no client passed; browser client only on client. */
function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return require('@/lib/supabase/browser-client').supabase as SupabaseClient<Database>
}

const getAuthenticatedFetch = (): AuthenticatedFetch => {
  const mod = require('@/lib/auth/authenticated-fetch') as {
    authenticatedFetch: AuthenticatedFetch
  }

  return mod.authenticatedFetch
}

async function readJsonError(response: Response, fallbackMessage: string) {
  const responseText = await response.text().catch(() => '')

  if (!responseText) {
    return fallbackMessage
  }

  try {
    const payload = JSON.parse(responseText) as { error?: string }
    return payload.error || fallbackMessage
  } catch {
    return responseText
  }
}

async function readBrowserWorkspaceChatPage(
  workspaceId: string,
  options: BrowserWorkspaceChatReadOptions = {}
) {
  const searchParams = new URLSearchParams()

  if (options.all) {
    searchParams.set('all', 'true')
  }

  if (typeof options.limit === 'number' && options.limit > 0) {
    searchParams.set('limit', String(options.limit))
  }

  if (typeof options.offset === 'number' && options.offset >= 0) {
    searchParams.set('offset', String(options.offset))
  }

  const normalizedCursor = options.cursor?.trim()
  if (normalizedCursor) {
    searchParams.set('cursor', normalizedCursor)
  }

  if (typeof options.pinned === 'boolean') {
    searchParams.set('pinned', String(options.pinned))
  }

  const queryString = searchParams.toString()
  const response = await getAuthenticatedFetch()(
    `/api/chat/workspaces/${encodeURIComponent(workspaceId)}/chats${queryString ? `?${queryString}` : ''}`,
    {
      cache: 'no-store',
      credentials: 'include'
    }
  )

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to load chats'))
  }

  return (await response.json()) as WorkspaceChatPage
}

async function readBrowserWorkspaceChatSearchResults(
  workspaceId: string,
  query: string,
  options: BrowserWorkspaceChatSearchOptions = {}
) {
  const searchParams = new URLSearchParams({
    query
  })

  if (options.nameOnly) {
    searchParams.set('nameOnly', 'true')
  }

  const response = await getAuthenticatedFetch()(
    `/api/chat/workspaces/${encodeURIComponent(workspaceId)}/chats?${searchParams.toString()}`,
    {
      cache: 'no-store',
      credentials: 'include'
    }
  )

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to search chats'))
  }

  const payload = (await response.json()) as {
    results?: ChatSearchRow[]
  }

  return Array.isArray(payload.results) ? payload.results : []
}

function normalizePositiveInteger(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback
  }

  return Math.min(Math.trunc(value), 200)
}

function normalizeOffset(value: number | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return 0
  }

  return Math.trunc(value)
}

function normalizeCursor(value?: string | null) {
  const trimmedValue = value?.trim()
  return trimmedValue ? trimmedValue : null
}

const CHAT_ACTIVITY_ORDER = {
  ascending: false,
  nullsFirst: false
} as const

function withChatActivityTimestamp(
  chat: TablesInsert<'chats'>
): TablesInsert<'chats'> {
  const createdAt = chat.created_at ?? new Date().toISOString()

  return {
    ...chat,
    created_at: createdAt,
    updated_at: chat.updated_at ?? createdAt
  }
}

function hasActiveChatMembership(status: string | null | undefined) {
  return !status || ACTIVE_CHAT_MEMBER_STATUSES.has(status)
}

function uniqueStrings(values: Array<string | null | undefined>) {
  return Array.from(
    new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
    )
  )
}

function buildInFilter(column: string, values: string[]) {
  const normalizedValues = uniqueStrings(values)

  return normalizedValues.length > 0
    ? `${column}.in.(${normalizedValues.join(',')})`
    : null
}

/** Attach normalized `space_icon` for Space-scoped chats (one query, no N+1). */
async function enrichChatsWithSpaceIcons(
  chats: ChatRow[],
  client: SupabaseClient<Database>
): Promise<ChatWithSpaceIcon[]> {
  if (chats.length === 0) {
    return chats
  }

  // Prefer chats.space_id; fall back to space_chats link when scope wasn't mirrored.
  const linkedSpaceIdsByChatId = await getLinkedSpaceIdsByChatId(chats, client)
  const spaceIds = uniqueStrings(Array.from(linkedSpaceIdsByChatId.values()))

  if (spaceIds.length === 0) {
    return chats
  }

  const { data, error } = await client
    .from('spaces')
    .select('id, icon')
    .in('id', spaceIds)
    .is('deleted_at', null)

  if (error) {
    isDev && console.error('enrichChatsWithSpaceIcons:', error)
    return chats
  }

  const iconBySpaceId = new Map(
    ((data ?? []) as Array<{ id: string; icon: string | null }>).map((space) => [
      space.id,
      normalizeSpaceEmojiIcon(space.icon)
    ])
  )

  return chats.map((chat) => {
    const linkedSpaceId = linkedSpaceIdsByChatId.get(chat.id)
    if (!linkedSpaceId) {
      return chat
    }

    return {
      ...chat,
      space_id: chat.space_id ?? linkedSpaceId,
      space_icon: iconBySpaceId.get(linkedSpaceId) ?? null
    }
  })
}

const SPACE_CONVERSATION_CHAT_DEFAULTS = {
  context_length: 4096,
  embeddings_provider: 'openai',
  include_profile_context: true,
  include_workspace_instructions: true,
  model: 'losi-omega',
  prompt: '',
  sharing: 'private',
  temperature: 0.5,
  type: 'space_conversation'
} as const

function chatActivityTimestampMs(
  chat: Pick<ChatRow, 'updated_at' | 'created_at'> | {
    updated_at?: string | null
    created_at?: string | null
  }
) {
  const timestamp = Date.parse(chat.updated_at ?? chat.created_at ?? '')
  return Number.isFinite(timestamp) ? timestamp : 0
}

/**
 * Ensure a `chats` row exists for a Space conversation so it appears in the
 * main workspace chat list (native + web sidebar). Idempotent upsert by id.
 *
 * IMPORTANT: `chats` has a BEFORE UPDATE trigger that forces `updated_at = NOW()`
 * unless the column is explicitly changed to a different value. List/merge
 * must NOT update existing rows — that re-bumped every Space chat to the top
 * of the sidebar on each load. Pass `touchActivity: true` only from real
 * message/create paths that should advance activity.
 */
export async function ensureSpaceConversationFeatureChat(
  args: {
    conversationId: string
    conversationName: string
    spaceId: string
    workspaceId: string
    userId: string
    model?: string | null
    updatedAt?: string | null
    createdAt?: string | null
    /** When true, persist name/meta and advance chats.updated_at (real activity). */
    touchActivity?: boolean
  },
  client?: SupabaseClient<Database>
): Promise<ChatRow | null> {
  const conversationId = args.conversationId.trim()
  const spaceId = args.spaceId.trim()
  const workspaceId = args.workspaceId.trim()
  const userId = args.userId.trim()
  if (!conversationId || !spaceId || !workspaceId || !userId) {
    return null
  }

  const db = client ?? getDefaultClient()
  const name =
    args.conversationName.trim() ||
    'Untitled chat'
  const createdAt =
    args.createdAt?.trim() ||
    args.updatedAt?.trim() ||
    new Date().toISOString()
  const updatedAt = args.updatedAt?.trim() || createdAt
  const model =
    args.model?.trim() || SPACE_CONVERSATION_CHAT_DEFAULTS.model
  const touchActivity = args.touchActivity === true

  const { data: existing, error: existingError } = await db
    .from('chats')
    .select('*')
    .eq('id', conversationId)
    .maybeSingle()

  if (existingError) {
    isDev && console.error('ensureSpaceConversationFeatureChat read:', existingError)
    return null
  }

  if (existing) {
    // List/merge path: never UPDATE — trigger would force updated_at=NOW().
    if (!touchActivity) {
      return existing as ChatRow
    }

    const { data: updated, error: updateError } = await db
      .from('chats')
      .update({
        name,
        space_id: spaceId,
        workspace_id: workspaceId,
        type: 'space_conversation',
        // Explicit activity time (trigger preserves distinct updated_at).
        updated_at: updatedAt,
        metadata: {
          ...(typeof existing.metadata === 'object' &&
          existing.metadata &&
          !Array.isArray(existing.metadata)
            ? (existing.metadata as Record<string, unknown>)
            : {}),
          source: 'space_conversation',
          spaceId,
          conversationId
        }
      })
      .eq('id', conversationId)
      .select('*')
      .single()

    if (updateError) {
      isDev && console.error('ensureSpaceConversationFeatureChat update:', updateError)
      return existing as ChatRow
    }

    return updated as ChatRow
  }

  const insertPayload = withChatActivityTimestamp({
    id: conversationId,
    user_id: userId,
    workspace_id: workspaceId,
    space_id: spaceId,
    assistant_id: null,
    context_length: SPACE_CONVERSATION_CHAT_DEFAULTS.context_length,
    embeddings_provider: SPACE_CONVERSATION_CHAT_DEFAULTS.embeddings_provider,
    include_profile_context:
      SPACE_CONVERSATION_CHAT_DEFAULTS.include_profile_context,
    include_workspace_instructions:
      SPACE_CONVERSATION_CHAT_DEFAULTS.include_workspace_instructions,
    last_shared_message_id: null,
    metadata: {
      source: 'space_conversation',
      spaceId,
      conversationId
    },
    model,
    name,
    prompt: SPACE_CONVERSATION_CHAT_DEFAULTS.prompt,
    shared_at: null,
    shared_by: null,
    sharing: SPACE_CONVERSATION_CHAT_DEFAULTS.sharing,
    temperature: SPACE_CONVERSATION_CHAT_DEFAULTS.temperature,
    type: SPACE_CONVERSATION_CHAT_DEFAULTS.type,
    created_at: createdAt,
    updated_at: updatedAt
  })

  const { data: created, error: insertError } = await db
    .from('chats')
    .insert([insertPayload])
    .select('*')
    .single()

  if (!insertError && created) {
    return created as ChatRow
  }

  // Race: another request inserted the same id — re-read.
  const { data: raced } = await db
    .from('chats')
    .select('*')
    .eq('id', conversationId)
    .maybeSingle()
  if (raced) return raced as ChatRow
  isDev && console.error('ensureSpaceConversationFeatureChat insert:', insertError)
  return null
}

/**
 * Pull recent Space conversations into the workspace chat list when they are
 * missing a `chats` row (native never created one; web only did on mount).
 *
 * Does not bump activity timestamps. Only injects rows that belong in the
 * current page's activity window so Space chats don't outrank normal chats.
 */
async function mergeSpaceConversationsIntoWorkspaceChats(
  workspaceId: string,
  userId: string,
  chats: ChatWithSpaceIcon[],
  client: SupabaseClient<Database>,
  options?: { limit?: number; pinnedOnly?: boolean }
): Promise<ChatWithSpaceIcon[]> {
  // Pinned Space conversations are rare and not a separate pin model — skip.
  if (options?.pinnedOnly) {
    return chats
  }

  const limit = normalizePositiveInteger(options?.limit, 50)

  const { data: spaces, error: spacesError } = await client
    .from('spaces')
    .select('id, icon, user_id, sharing, workspace_id')
    .eq('workspace_id', workspaceId)
    .is('deleted_at', null)

  if (spacesError) {
    isDev && console.error('mergeSpaceConversationsIntoWorkspaceChats spaces:', spacesError)
    return chats
  }

  const spaceRows = (spaces ?? []) as Array<{
    id: string
    icon: string | null
    user_id: string
    sharing: string | null
    workspace_id: string
  }>
  if (spaceRows.length === 0) {
    return chats
  }

  const accessContext = await getWorkspaceChatAccessContext(
    workspaceId,
    userId,
    client
  )
  const readableSpaceIds = new Set<string>()
  const privateSpaceIds: string[] = []

  for (const space of spaceRows) {
    if (
      accessContext.workspaceRole === 'owner' ||
      accessContext.workspaceRole === 'admin' ||
      space.user_id === userId
    ) {
      readableSpaceIds.add(space.id)
      continue
    }
    if (
      space.sharing === 'workspace' &&
      hasWorkspaceWideChatRead(accessContext.workspaceRole)
    ) {
      readableSpaceIds.add(space.id)
      continue
    }
    privateSpaceIds.push(space.id)
  }

  if (privateSpaceIds.length > 0) {
    const memberReadable = await getReadableSpaceMemberIds(
      privateSpaceIds,
      userId,
      client
    )
    for (const spaceId of memberReadable) {
      readableSpaceIds.add(spaceId)
    }
  }

  if (readableSpaceIds.size === 0) {
    return chats
  }

  const { data: conversations, error: conversationsError } = await client
    .from('space_conversations')
    .select('id, name, space_id, updated_at, created_at, user_id, model, archived')
    .in('space_id', Array.from(readableSpaceIds))
    .eq('archived', false)
    .order('updated_at', { ascending: false })
    .limit(limit)

  if (conversationsError) {
    isDev &&
      console.error(
        'mergeSpaceConversationsIntoWorkspaceChats conversations:',
        conversationsError
      )
    return chats
  }

  const conversationRows = (conversations ?? []) as Array<{
    id: string
    name: string
    space_id: string
    updated_at: string | null
    created_at: string
    user_id: string
    model: string | null
    archived: boolean | null
  }>

  if (conversationRows.length === 0) {
    return chats
  }

  const conversationById = new Map(
    conversationRows.map((conversation) => [conversation.id, conversation])
  )
  const iconBySpaceId = new Map(
    spaceRows.map((space) => [space.id, normalizeSpaceEmojiIcon(space.icon)])
  )

  // Prefer space_conversations activity over chats.updated_at — list/merge used
  // to bump chats to NOW via the update trigger, which floated Space threads.
  const withConversationActivity = chats.map((chat) => {
    const conversation = conversationById.get(chat.id)
    if (!conversation) {
      return chat
    }
    const activityAt = conversation.updated_at ?? conversation.created_at
    if (!activityAt) {
      return chat
    }
    return {
      ...chat,
      name: conversation.name || chat.name,
      space_id: chat.space_id ?? conversation.space_id,
      space_icon:
        chat.space_icon ?? iconBySpaceId.get(conversation.space_id) ?? null,
      updated_at: activityAt
    }
  })

  const existingIds = new Set(withConversationActivity.map((chat) => chat.id))
  const pageIsFull = withConversationActivity.length >= limit
  const pageFloor = pageIsFull
    ? Math.min(
        ...withConversationActivity.map((chat) => chatActivityTimestampMs(chat))
      )
    : 0
  const merged = [...withConversationActivity]

  for (const conversation of conversationRows) {
    if (existingIds.has(conversation.id)) {
      continue
    }

    const activityAt = conversation.updated_at ?? conversation.created_at
    // Insert-if-missing only — never touchActivity on list/merge (avoids NOW bump).
    const ensured = await ensureSpaceConversationFeatureChat(
      {
        conversationId: conversation.id,
        conversationName: conversation.name,
        spaceId: conversation.space_id,
        workspaceId,
        userId: conversation.user_id || userId,
        model: conversation.model,
        updatedAt: activityAt,
        createdAt: conversation.created_at,
        touchActivity: false
      },
      client
    )

    if (!ensured) {
      continue
    }

    const listRow: ChatWithSpaceIcon = {
      ...ensured,
      space_id: ensured.space_id ?? conversation.space_id,
      space_icon: iconBySpaceId.get(conversation.space_id) ?? null,
      // Rank by conversation activity even if the chats row is still inflated.
      updated_at: activityAt || ensured.updated_at
    }

    // Full pages: only surface threads that already rank in this activity window.
    if (pageIsFull && chatActivityTimestampMs(listRow) < pageFloor) {
      continue
    }

    existingIds.add(listRow.id)
    merged.push(listRow)
  }

  const sorted = merged.sort((left, right) => {
    return chatActivityTimestampMs(right) - chatActivityTimestampMs(left)
  })

  // Keep page size honest so Space backfill cannot flood the first page.
  return pageIsFull ? sorted.slice(0, limit) : sorted
}

function hasWorkspaceWideChatRead(role: string | null | undefined) {
  return Boolean(role && role !== 'guest')
}

function buildReadableWorkspaceChatFilter(
  userId: string,
  scope: WorkspaceChatReadScope
) {
  const conditions = [`user_id.eq.${userId}`]

  if (hasWorkspaceWideChatRead(scope.workspaceRole)) {
    conditions.push('sharing.eq.public')
  }

  const chatIdsFilter = buildInFilter('id', scope.chatIds)

  if (chatIdsFilter) {
    conditions.push(chatIdsFilter)
  }

  return conditions.join(',')
}

async function getWorkspaceChatAccessContext(
  workspaceId: string,
  userId: string,
  client: SupabaseClient<Database>
): Promise<WorkspaceChatAccessContext> {
  return {
    workspaceRole: await getWorkspaceRoleForUser(client, workspaceId, userId)
  }
}

async function getActiveChatMemberChatIds(
  userId: string,
  client: SupabaseClient<Database>
) {
  const { data, error } = await client
    .from('chat_members')
    .select('chat_id, status')
    .eq('user_id', userId)

  if (error) {
    throw new Error(error.message)
  }

  return uniqueStrings(
    ((data ?? []) as ChatMemberAccessRow[])
      .filter((member) => hasActiveChatMembership(member.status))
      .map((member) => member.chat_id)
  )
}

async function getWorkspaceChatReadScope(
  workspaceId: string,
  userId: string,
  client: SupabaseClient<Database>
): Promise<WorkspaceChatReadScope> {
  const accessContext = await getWorkspaceChatAccessContext(
    workspaceId,
    userId,
    client
  )

  if (
    accessContext.workspaceRole === 'owner' ||
    accessContext.workspaceRole === 'admin'
  ) {
    return {
      chatIds: [],
      workspaceRole: accessContext.workspaceRole
    }
  }

  const memberChatIds = await getActiveChatMemberChatIds(userId, client)

  return {
    chatIds: memberChatIds,
    workspaceRole: accessContext.workspaceRole
  }
}

async function getActiveChatMemberIds(
  chatIds: string[],
  userId: string,
  client: SupabaseClient<Database>
) {
  if (chatIds.length === 0) {
    return new Set<string>()
  }

  const { data, error } = await client
    .from('chat_members')
    .select('chat_id, status')
    .in('chat_id', chatIds)
    .eq('user_id', userId)

  if (error) {
    throw new Error(error.message)
  }

  return new Set(
    ((data ?? []) as ChatMemberAccessRow[])
      .filter((member) => hasActiveChatMembership(member.status))
      .map((member) => member.chat_id)
  )
}

async function getLinkedSpaceIdsByChatId(
  chats: ChatRow[],
  client: SupabaseClient<Database>
) {
  const linkedSpaceIdsByChatId = new Map<string, string>()
  const chatIdsMissingSpaceId: string[] = []

  for (const chat of chats) {
    if (chat.space_id) {
      linkedSpaceIdsByChatId.set(chat.id, chat.space_id)
      continue
    }

    chatIdsMissingSpaceId.push(chat.id)
  }

  if (chatIdsMissingSpaceId.length === 0) {
    return linkedSpaceIdsByChatId
  }

  const { data, error } = await client
    .from('space_chats')
    .select('chat_id, space_id')
    .in('chat_id', chatIdsMissingSpaceId)

  if (error) {
    throw new Error(error.message)
  }

  for (const link of (data ?? []) as SpaceChatAccessRow[]) {
    linkedSpaceIdsByChatId.set(link.chat_id, link.space_id)
  }

  return linkedSpaceIdsByChatId
}

async function getSpacesById(
  spaceIds: string[],
  client: SupabaseClient<Database>
) {
  if (spaceIds.length === 0) {
    return new Map<string, SpaceAccessRow>()
  }

  const { data, error } = await client
    .from('spaces')
    .select('id, user_id, sharing')
    .in('id', spaceIds)
    .is('deleted_at', null)

  if (error) {
    throw new Error(error.message)
  }

  return new Map(
    ((data ?? []) as SpaceAccessRow[]).map((space) => [space.id, space])
  )
}

async function getReadableSpaceMemberIds(
  spaceIds: string[],
  userId: string,
  client: SupabaseClient<Database>
) {
  if (spaceIds.length === 0) {
    return new Set<string>()
  }

  const { data, error } = await client
    .from('space_members')
    .select('space_id, role')
    .in('space_id', spaceIds)
    .eq('user_id', userId)

  if (error) {
    throw new Error(error.message)
  }

  return new Set(
    ((data ?? []) as SpaceMemberAccessRow[])
      .filter((member) => Boolean(member.role))
      .map((member) => member.space_id)
  )
}

async function filterWorkspaceChatsByReadAccess(
  chats: ChatRow[],
  userId: string,
  workspaceId: string,
  client: SupabaseClient<Database>,
  context?: WorkspaceChatAccessContext
) {
  if (chats.length === 0) {
    return []
  }

  const accessContext =
    context ?? (await getWorkspaceChatAccessContext(workspaceId, userId, client))

  if (
    accessContext.workspaceRole === 'owner' ||
    accessContext.workspaceRole === 'admin'
  ) {
    return chats
  }

  const activeChatMemberIds = await getActiveChatMemberIds(
    chats.map((chat) => chat.id),
    userId,
    client
  )
  const directReadableChats = new Set<string>()
  const linkedSpaceCandidateChats: ChatRow[] = []

  for (const chat of chats) {
    if (chat.user_id === userId || activeChatMemberIds.has(chat.id)) {
      directReadableChats.add(chat.id)
      continue
    }

    if (chat.is_group_chat) {
      continue
    }

    linkedSpaceCandidateChats.push(chat)
  }

  const linkedSpaceIdsByChatId = await getLinkedSpaceIdsByChatId(
    linkedSpaceCandidateChats,
    client
  )
  const linkedSpaceIds = Array.from(new Set(linkedSpaceIdsByChatId.values()))
  const spacesById = await getSpacesById(linkedSpaceIds, client)
  const privateSpaceIds = Array.from(
    new Set(
      linkedSpaceIds.filter((spaceId) => {
        const space = spacesById.get(spaceId)
        return (
          space &&
          space.user_id !== userId &&
          space.sharing !== 'workspace'
        )
      })
    )
  )
  const readableSpaceMemberIds = await getReadableSpaceMemberIds(
    privateSpaceIds,
    userId,
    client
  )

  return chats.filter((chat) => {
    if (directReadableChats.has(chat.id)) {
      return true
    }

    const linkedSpaceId = linkedSpaceIdsByChatId.get(chat.id)
    if (!linkedSpaceId) {
      return (
        chat.sharing === 'public' &&
        hasWorkspaceWideChatRead(accessContext.workspaceRole)
      )
    }

    const linkedSpace = spacesById.get(linkedSpaceId)
    if (!linkedSpace) {
      return false
    }

    if (linkedSpace.user_id === userId) {
      return true
    }

    if (linkedSpace.sharing === 'workspace') {
      return hasWorkspaceWideChatRead(accessContext.workspaceRole)
    }

    return readableSpaceMemberIds.has(linkedSpace.id)
  })
}

async function readBrowserJsonError(response: Response, fallbackMessage: string) {
  const responseText = await response.text().catch(() => '')

  if (!responseText) {
    return fallbackMessage
  }

  try {
    const payload = JSON.parse(responseText) as {
      error?: string
      message?: string
    }
    return payload.error || payload.message || fallbackMessage
  } catch {
    return responseText
  }
}

async function touchBrowserChatActivity(
  chatId: string,
  updatedAt?: string,
  model?: string
) {
  const response = await getAuthenticatedFetch()(`/api/chat/${encodeURIComponent(chatId)}/touch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      ...(updatedAt ? { updatedAt } : {}),
      ...(model ? { model } : {})
    })
  })

  if (!response.ok) {
    throw new Error(await readBrowserJsonError(response, 'Failed to refresh chat activity'))
  }

  const updatedChat = (await response.json()) as Tables<'chats'>
  notifySidebarChatUpdated(updatedChat)

  return updatedChat
}

async function updateBrowserChat(
  chatId: string,
  chat: TablesUpdate<'chats'>
) {
  const response = await getAuthenticatedFetch()(`/api/chat/${encodeURIComponent(chatId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(chat)
  })

  if (!response.ok) {
    throw new Error(await readBrowserJsonError(response, 'Failed to update chat'))
  }

  const updatedChat = (await response.json()) as Tables<'chats'>
  notifySidebarChatUpdated(updatedChat)

  return updatedChat
}

async function createBrowserChat(chat: TablesInsert<'chats'>) {
  const workspaceId = chat.workspace_id?.trim()

  if (!workspaceId) {
    throw new Error('workspace_id required')
  }

  const response = await getAuthenticatedFetch()(
    `/api/chat/workspaces/${encodeURIComponent(workspaceId)}/chats`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(chat)
    }
  )

  if (!response.ok) {
    throw new Error(await readBrowserJsonError(response, 'Failed to create chat'))
  }

  const createdChat = (await response.json()) as Tables<'chats'>
  notifySidebarChatCreated(createdChat)

  return createdChat
}

async function getLinkedSpaceForChat(
  chat: ChatRow,
  client: SupabaseClient<Database>
) {
  let linkedSpaceId = chat.space_id ?? null

  if (!linkedSpaceId) {
    const { data: linkedSpace, error: linkedSpaceError } = await client
      .from('space_chats')
      .select('space_id')
      .eq('chat_id', chat.id)
      .maybeSingle()

    if (linkedSpaceError && linkedSpaceError.code !== 'PGRST116') {
      throw new Error(linkedSpaceError.message)
    }

    linkedSpaceId = linkedSpace?.space_id ?? null
  }

  if (!linkedSpaceId) {
    return null
  }

  const { data: space, error: spaceError } = await client
    .from('spaces')
    .select('id, user_id, sharing')
    .eq('id', linkedSpaceId)
    .is('deleted_at', null)
    .maybeSingle()

  if (spaceError) {
    throw new Error(spaceError.message)
  }

  return space
}

async function getChatMemberRecord(
  chatId: string,
  userId: string,
  client: SupabaseClient<Database>
) {
  const { data, error } = await client
    .from('chat_members')
    .select('role, status')
    .eq('chat_id', chatId)
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

async function getSpaceMemberRole(
  spaceId: string,
  userId: string,
  client: SupabaseClient<Database>
) {
  const { data, error } = await client
    .from('space_members')
    .select('role')
    .eq('space_id', spaceId)
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data?.role ?? null
}

async function canUserReadChat(
  chat: ChatRow,
  userId: string,
  client: SupabaseClient<Database>
) {
  if (chat.user_id === userId) {
    return true
  }

  const workspaceRole =
    chat.workspace_id
      ? await getWorkspaceRoleForUser(client, chat.workspace_id, userId)
      : null

  if (workspaceRole === 'owner' || workspaceRole === 'admin') {
    return true
  }

  const chatMember = await getChatMemberRecord(chat.id, userId, client)
  if (chatMember && hasActiveChatMembership(chatMember.status)) {
    return true
  }

  if (chat.is_group_chat) {
    return false
  }

  const linkedSpace = await getLinkedSpaceForChat(chat, client)

  if (linkedSpace) {
    if (linkedSpace.user_id === userId) {
      return true
    }

    if (linkedSpace.sharing === 'workspace') {
      return hasWorkspaceWideChatRead(workspaceRole)
    }

    const spaceRole = await getSpaceMemberRole(linkedSpace.id, userId, client)
    return Boolean(spaceRole)
  }

  return chat.sharing === 'public' && hasWorkspaceWideChatRead(workspaceRole)
}

async function canUserManageChat(
  chat: ChatRow,
  userId: string,
  client: SupabaseClient<Database>
) {
  if (chat.user_id === userId) {
    return true
  }

  const workspaceRole =
    chat.workspace_id
      ? await getWorkspaceRoleForUser(client, chat.workspace_id, userId)
      : null

  if (workspaceRole === 'owner' || workspaceRole === 'admin') {
    return true
  }

  const chatMember = await getChatMemberRecord(chat.id, userId, client)

  if (chatMember && hasActiveChatMembership(chatMember.status)) {
    if (chat.is_group_chat) {
      return GROUP_CHAT_MANAGE_ROLES.has(chatMember.role ?? '')
    }

    return WRITEABLE_SPACE_ROLES.has(chatMember.role ?? '')
  }

  if (chat.is_group_chat) {
    return false
  }

  if (!chatMember || !hasActiveChatMembership(chatMember.status)) {
    const linkedSpace = await getLinkedSpaceForChat(chat, client)

    if (!linkedSpace) {
      return false
    }

    if (linkedSpace.user_id === userId) {
      return true
    }

    if (linkedSpace.sharing === 'workspace') {
      return workspaceRole === 'member'
    }

    const spaceRole = await getSpaceMemberRole(linkedSpace.id, userId, client)
    return spaceRole ? WRITEABLE_SPACE_ROLES.has(spaceRole) : false
  }

  return false
}

export async function filterChatsByReadAccess(
  chats: ChatRow[],
  userId: string,
  client?: SupabaseClient<Database>
) {
  const db = client ?? getDefaultClient()
  const allowedChats: ChatRow[] = []

  for (const chat of chats) {
    if (await canUserReadChat(chat, userId, db)) {
      allowedChats.push(chat)
    }
  }

  return allowedChats
}

export async function getAccessibleChatsByWorkspaceForUser(
  workspaceId: string,
  userId: string,
  options?: {
    limit?: number
    offset?: number
    pinned?: boolean
    client?: SupabaseClient<Database>
  }
) {
  const db = options?.client ?? getDefaultClient()
  const readScope = await getWorkspaceChatReadScope(workspaceId, userId, db)
  let query = db
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })

  if (typeof options?.pinned === 'boolean') {
    query = query.eq('pinned', options.pinned)
  }

  if (
    readScope.workspaceRole !== 'owner' &&
    readScope.workspaceRole !== 'admin'
  ) {
    query = query.or(buildReadableWorkspaceChatFilter(userId, readScope))
  }

  if (options?.limit) {
    query = query.limit(options.limit)
  }

  if (typeof options?.offset === 'number') {
    query = query.range(
      options.offset,
      options.offset + (options.limit || 50) - 1
    )
  }

  const { data, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  const enriched = await enrichChatsWithSpaceIcons((data ?? []) as ChatRow[], db)
  return mergeSpaceConversationsIntoWorkspaceChats(
    workspaceId,
    userId,
    enriched,
    db,
    { limit: options?.limit, pinnedOnly: options?.pinned === true }
  )
}

export async function getAccessibleWorkspaceChatsPageForUser(
  workspaceId: string,
  userId: string,
  options?: AccessibleWorkspaceChatPageOptions
): Promise<WorkspaceChatPage> {
  const db = options?.client ?? getDefaultClient()
  const limit = normalizePositiveInteger(options?.limit, 50)
  const requestedCursor = normalizeCursor(options?.cursor)
  const pinned = options?.pinned
  const accessibleOffset = requestedCursor ? 0 : normalizeOffset(options?.offset)
  const readScope = await getWorkspaceChatReadScope(workspaceId, userId, db)
  let query = db
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })

  if (typeof pinned === 'boolean') {
    query = query.eq('pinned', pinned)
  }

  if (
    readScope.workspaceRole !== 'owner' &&
    readScope.workspaceRole !== 'admin'
  ) {
    query = query.or(buildReadableWorkspaceChatFilter(userId, readScope))
  }

  if (requestedCursor) {
    query = query.lt('updated_at', requestedCursor).limit(limit + 1)
  } else {
    query = query.range(accessibleOffset, accessibleOffset + limit)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  const rows = (data ?? []) as ChatRow[]
  const hasMore = rows.length > limit
  const pageRows = hasMore ? rows.slice(0, limit) : rows
  const enriched = await enrichChatsWithSpaceIcons(pageRows, db)
  const chats = await mergeSpaceConversationsIntoWorkspaceChats(
    workspaceId,
    userId,
    enriched,
    db,
    { limit, pinnedOnly: pinned === true }
  )

  return {
    chats,
    hasMore,
    nextCursor: hasMore ? chats[chats.length - 1]?.updated_at ?? null : null
  }
}

export async function canUserManageChatById(
  chatId: string,
  userId: string,
  client?: SupabaseClient<Database>
) {
  if (!chatId?.trim() || !userId?.trim()) {
    return false
  }

  const db = client ?? getDefaultClient()
  const chat = await getChatById(chatId, db)

  if (!chat) {
    return false
  }

  return canUserManageChat(chat, userId, db)
}

export async function canUserWriteMessagesToChatById(
  chatId: string,
  userId: string,
  client?: SupabaseClient<Database>
) {
  if (!chatId?.trim() || !userId?.trim()) {
    return false
  }

  const db = client ?? getDefaultClient()
  const chat = await getChatById(chatId, db)

  if (!chat) {
    return false
  }

  if (await canUserManageChat(chat, userId, db)) {
    return true
  }

  if (!chat.is_group_chat) {
    return false
  }

  return canUserReadChat(chat, userId, db)
}

export const getChatById = async (chatId: string, client?: SupabaseClient<Database>) => {
  const c = client ?? getDefaultClient()
  const { data } = await c.from('chats').select('*').eq('id', chatId).maybeSingle()
  return data as Tables<'chats'> | null
}

export const getChatByIdForUser = async (
  chatId: string,
  userId: string,
  client?: SupabaseClient<Database>
) => {
  if (!chatId?.trim() || !userId?.trim()) {
    return null
  }

  const c = client ?? getDefaultClient()
  const chat = await getChatById(chatId, c)

  if (!chat) {
    return null
  }

  return (await canUserReadChat(chat, userId, c)) ? chat : null
}

// Enhanced search: chat name (ILIKE) + message content (full-text via RPC).
// Run migration 20260202000001_messages_fulltext_search.sql for FTS.
// Same method used by sidebar and chat/message search tool for consistent FTS.
export const searchChatsAndMessagesWithSnippets = async (
  workspaceId: string,
  query: string,
  options?: { nameOnly?: boolean; client?: SupabaseClient<Database> }
): Promise<{
  data: ChatSearchRow[] | null
  error: any
}> => {
  if (!options?.client && typeof window !== 'undefined') {
    try {
      if (!workspaceId || !query.trim()) {
        return { data: [], error: null }
      }

      const results = await readBrowserWorkspaceChatSearchResults(
        workspaceId,
        query.trim(),
        {
          nameOnly: options?.nameOnly
        }
      )

      return {
        data: results,
        error: null
      }
    } catch (error) {
      isDev && console.error('Enhanced browser search error:', error)
      return { data: null, error }
    }
  }

  const client = getDefaultClient(options?.client)
  try {
    if (!workspaceId || !query.trim()) {
      return { data: [], error: null }
    }

    const nameOnly = options?.nameOnly === true

    // 1. Chat name search (fast, indexed)
    const nameMatchResult = await client
      .from('chats')
      .select('*')
      .eq('workspace_id', workspaceId)
      .ilike('name', `%${query}%`)
      .limit(25)

    if (nameMatchResult.error) return { data: null, error: nameMatchResult.error }

    const matchesByChat: Record<string, Array<{ message_id: string; content: string }>> = {}

    if (!nameOnly) {
      // 2. Message full-text search: one RPC, index-backed, ranked by relevance
      const { data: ftsRows, error: ftsError } = await (
        client as SupabaseClient<Database>
      ).rpc('search_messages_in_workspace', {
        p_workspace_id: workspaceId,
        p_query: query.trim(),
        p_limit: 40
      })

      const ftsList = Array.isArray(ftsRows) ? ftsRows : []
      if (!ftsError && ftsList.length > 0) {
        for (const row of ftsList as Array<{
          message_id: string
          chat_id: string
          content_snippet: string | null
        }>) {
          if (!row.chat_id) continue
          if (!matchesByChat[row.chat_id]) matchesByChat[row.chat_id] = []
          matchesByChat[row.chat_id].push({
            message_id: row.message_id,
            content: row.content_snippet ?? ''
          })
        }
      }
    }

    const chatIdsToFetch = new Set<string>()
    const nameMatchChats = (nameMatchResult.data || []) as Tables<'chats'>[]
    nameMatchChats.forEach((c) => chatIdsToFetch.add(c.id))
    Object.keys(matchesByChat).forEach((id) => chatIdsToFetch.add(id))

    if (chatIdsToFetch.size === 0) return { data: [], error: null }

    const { data: chats, error: chatsError } = await client
      .from('chats')
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('id', Array.from(chatIdsToFetch))

    if (chatsError) return { data: null, error: chatsError }

    const chatsList = (chats || []) as Tables<'chats'>[]
    const results = chatsList.map((chat) => ({
      chat,
      matches: matchesByChat[chat.id] || []
    }))

    const sorted = results.sort((a, b) => {
      const da = new Date(a.chat.updated_at ?? a.chat.created_at)
      const db = new Date(b.chat.updated_at ?? b.chat.created_at)
      return db.getTime() - da.getTime()
    })

    return { data: sorted.slice(0, 50), error: null }
  } catch (error) {
    isDev && console.error('Enhanced search error:', error)
    return { data: null, error }
  }
}

export const searchChatsAndMessagesWithSnippetsForUser = async (
  workspaceId: string,
  userId: string,
  query: string,
  options?: { nameOnly?: boolean; client?: SupabaseClient<Database> }
) => {
  const result = await searchChatsAndMessagesWithSnippets(
    workspaceId,
    query,
    options
  )

  if (!result.data) {
    return result
  }

  const db = options?.client ?? getDefaultClient()
  const accessibleChats = await filterChatsByReadAccess(
    result.data.map((row) => row.chat),
    userId,
    db
  )
  const accessibleChatIds = new Set(accessibleChats.map((chat) => chat.id))
  const enrichedChats = await enrichChatsWithSpaceIcons(accessibleChats, db)
  const spaceIconByChatId = new Map(
    enrichedChats.map((chat) => [chat.id, chat.space_icon ?? null])
  )

  return {
    data: result.data
      .filter((row) => accessibleChatIds.has(row.chat.id))
      .map((row) => ({
        ...row,
        chat: {
          ...row.chat,
          space_icon: spaceIconByChatId.get(row.chat.id) ?? null
        }
      })),
    error: result.error
  }
}

export const getChatsByWorkspaceId = async (
  workspaceId: string,
  limit?: number,
  offset?: number
) => {
  if (typeof window !== 'undefined') {
    const result = await readBrowserWorkspaceChatPage(workspaceId, {
      all: typeof limit !== 'number' && typeof offset !== 'number',
      limit,
      offset
    })

    return result.chats
  }

  const client = getDefaultClient()
  let query = client
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })

  if (limit) {
    query = query.limit(limit)
  }
  if (offset) {
    query = query.range(offset, offset + (limit || 50) - 1)
  }

  const { data: chats, error } = await query

  if (!chats) {
    throw new Error(error.message)
  }

  return enrichChatsWithSpaceIcons(chats, client)
}

/** Fetch Sidekick chats for a user (by user_id). Use when workspace context may be missing. */
export const getSidekickChatsForUser = async (
  userId: string,
  limit = 50
): Promise<Tables<'chats'>[]> => {
  const client = getDefaultClient()
  // Filter in DB so we only get rows already tagged as Sidekick (RPC sets type)
  const { data: chats, error } = await client
    .from('chats')
    .select('*')
    .eq('user_id', userId)
    .eq('type', 'sidekick')
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    isDev && console.error('getSidekickChatsForUser error:', error)
    throw new Error(error.message)
  }

  return (chats ?? []) as Tables<'chats'>[]
}

export const getChatsByWorkspaceIdPaginated = async (
  workspaceId: string,
  limit = 50,
  cursor?: string
) => {
  if (typeof window !== 'undefined') {
    return readBrowserWorkspaceChatPage(workspaceId, {
      limit,
      cursor
    })
  }

  const client = getDefaultClient()
  let query = client
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })
    .limit(limit + 1) // Get one extra to check if there are more

  if (cursor) {
    query = query.lt('updated_at', cursor)
  }

  const { data: chats, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  if (!chats) {
    return { chats: [], hasMore: false, nextCursor: null }
  }

  const chatsList = (chats || []) as Tables<'chats'>[]
  const hasMore = chatsList.length > limit
  const items = hasMore ? chatsList.slice(0, -1) : chatsList
  const nextCursor = hasMore ? items[items.length - 1]?.updated_at ?? null : null
  const enriched = await enrichChatsWithSpaceIcons(items, client)

  return {
    chats: enriched,
    hasMore,
    nextCursor
  }
}

export const createChat = async (chat: TablesInsert<'chats'>, client?: SupabaseClient<Database>) => {
  if (!client && typeof window !== 'undefined') {
    return createBrowserChat(chat)
  }

  const c = (client ?? getDefaultClient()) as SupabaseClient<Database>
  const chatWithActivityTimestamp = withChatActivityTimestamp(chat)
  const { data: createdChat, error } = await c
    .from('chats')
    .insert([chatWithActivityTimestamp])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  if (typeof window !== 'undefined') {
    notifySidebarChatCreated(createdChat)
  } else {
    // Index-on-write into the knowledge graph (server-only, non-blocking).
    void safeIndexEntity(
      'chat',
      createdChat as unknown as Record<string, unknown>,
      createdChat.workspace_id,
      c
    )
  }

  return createdChat
}

export const createChats = async (chats: TablesInsert<'chats'>[]) => {
  const client = getDefaultClient() as SupabaseClient<Database>
  const { data: createdChats, error } = await client
    .from('chats')
    .insert(chats.map(withChatActivityTimestamp))
    .select('*')

  if (error) {
    throw new Error(error.message)
  }

  if (typeof window !== 'undefined') {
    createdChats?.forEach(notifySidebarChatCreated)
  }

  return createdChats
}

export const updateChat = async (
  chatId: string,
  chat: TablesUpdate<'chats'>,
  client?: SupabaseClient<Database>
) => {
  if (!client && typeof window !== 'undefined') {
    return updateBrowserChat(chatId, chat)
  }

  const db = getDefaultClient(client) as SupabaseClient<Database>

  if (
    chat.sharing === 'public' ||
    chat.last_shared_message_id ||
    chat.shared_by ||
    chat.shared_at
  ) {
    const existingChat = await getChatById(chatId, db)

    if (existingChat?.is_group_chat) {
      throw new Error('Group chats cannot be shared publicly')
    }
  }

  const { data: updatedChat, error } = await db
    .from('chats')
    .update(chat)
    .eq('id', chatId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message || `Chat update failed: ${error.code} ${error.details || error.hint || ''}`)
  }

  if (typeof window !== 'undefined') {
    notifySidebarChatUpdated(updatedChat)
  }

  return updatedChat
}

export const touchChatActivity = async (
  chatId: string,
  updatedAt?: string,
  client?: SupabaseClient<Database>,
  model?: string
) => {
  if (!client && typeof window !== 'undefined') {
    return touchBrowserChatActivity(chatId, updatedAt, model)
  }

  const db = getDefaultClient(client) as SupabaseClient<Database>
  const nextUpdatedAt = updatedAt ?? new Date().toISOString()
  const { data: updatedChat, error } = await db
    .from('chats')
    .update({ updated_at: nextUpdatedAt, ...(model ? { model } : {}) })
    .eq('id', chatId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  if (typeof window !== 'undefined') {
    notifySidebarChatUpdated(updatedChat)
  }

  return updatedChat
}

/** Tag a chat as Sidekick via RPC so type/metadata persist (avoids client update issues). Returns rows updated (0 or 1). */
export const tagChatAsSidekick = async (chatId: string): Promise<number> => {
  const client = getDefaultClient()
  const { data, error } = await client.rpc('tag_chat_as_sidekick', { p_chat_id: chatId })
  if (error) throw new Error(error.message)
  return typeof data === 'number' ? data : 0
}

export const deleteChat = async (chatId: string) => {
  const client = getDefaultClient()
  const { error } = await client.from('chats').delete().eq('id', chatId)

  if (error) {
    throw new Error(error.message)
  }

  return true
}

export const getPinnedChatsByWorkspaceId = async (workspaceId: string) => {
  if (typeof window !== 'undefined') {
    const result = await readBrowserWorkspaceChatPage(workspaceId, {
      all: true,
      pinned: true
    })

    return result.chats
  }

  const client = getDefaultClient()
  const { data: chats, error } = await client
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('pinned', true)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })

  if (error) {
    isDev && console.error('Error fetching pinned chats:', error)
    throw new Error(error.message)
  }

  return enrichChatsWithSpaceIcons(chats || [], client)
}

export const getRecentUnpinnedChatsByWorkspaceId = async (
  workspaceId: string,
  limit: number = 20,
  offset: number = 0
) => {
  if (typeof window !== 'undefined') {
    const result = await readBrowserWorkspaceChatPage(workspaceId, {
      limit,
      offset,
      pinned: false
    })

    return result.chats
  }

  const client = getDefaultClient()
  const { data: chats, error } = await client
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('pinned', false)
    .order('updated_at', CHAT_ACTIVITY_ORDER)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) {
    isDev && console.error('Error fetching recent unpinned chats:', error)
    throw new Error(error.message)
  }

  return enrichChatsWithSpaceIcons(chats || [], client)
}

export const searchChatsAndMessages = async (
  workspaceId: string,
  query: string
): Promise<{ data: Tables<'chats'>[] | null; error: any }> => {
  try {
    if (typeof window !== 'undefined') {
      const result = await searchChatsAndMessagesWithSnippets(workspaceId, query)

      return {
        data: result.data?.map((row) => row.chat) ?? null,
        error: result.error
      }
    }

    // If no query, return empty results instead of all chats
    if (!workspaceId || !query.trim()) {
      return { data: [], error: null }
    }

    // Get both name matches and message matches in parallel for performance
    const client = getDefaultClient()
    const [nameMatchResult, messageMatchResult] = await Promise.all([
      // Search by chat name
      client
        .from('chats')
        .select('*')
        .eq('workspace_id', workspaceId)
        .ilike('name', `%${query}%`)
        .limit(25),

      // Search by message content with proper join to chats
      client
        .from('messages')
        .select(
          `
          chat_id,
          content,
          chats!messages_chat_id_fkey(workspace_id)
        `
        )
        .ilike('content', `%${query}%`)
        .eq('chats.workspace_id', workspaceId)
        .limit(25)
    ])

    if (nameMatchResult.error) {
      isDev && console.error('Name search error:', nameMatchResult.error)
      return { data: null, error: nameMatchResult.error }
    }

    if (messageMatchResult.error) {
      isDev && console.error('Message search error:', messageMatchResult.error)
      return { data: null, error: messageMatchResult.error }
    }

    // Get unique chat IDs from message matches
    const chatIdsFromMessages = [
      ...new Set(
        (messageMatchResult.data || [])
          .filter((msg) => (msg as any).chats?.workspace_id === workspaceId)
          .map((msg) => (msg as any).chat_id)
          .filter(Boolean)
      )
    ]

    // If we have message matches, fetch those chats
    let messageChats: Tables<'chats'>[] = []
    if (chatIdsFromMessages.length > 0) {
      const { data: chats, error: chatsError } = await client
        .from('chats')
        .select('*')
        .eq('workspace_id', workspaceId)
        .in('id', chatIdsFromMessages)

      if (chatsError) {
        isDev && console.error('Error fetching chats from messages:', chatsError)
        return { data: null, error: chatsError }
      }
      messageChats = chats || []
    }

    // Combine results
    const allResults = [...(nameMatchResult.data || []), ...messageChats]

    // Early return if no results
    if (allResults.length === 0) {
      return { data: [], error: null }
    }

    // Deduplicate by chat ID and maintain sort order
    const seen = new Set<string>()
    const uniqueResults = allResults.filter((chat) => {
      if (!chat || seen.has(chat.id)) return false
      seen.add(chat.id)
      return true
    })

    // Sort by most recently updated
    const sortedResults = uniqueResults.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at)
      const dateB = new Date(b.updated_at || b.created_at)
      return dateB.getTime() - dateA.getTime()
    })

    return {
      data: sortedResults.slice(0, 50),
      error: null
    }
  } catch (error) {
    isDev && console.error('Search error:', error)
    return { data: null, error }
  }
}

export const searchChatsByName = async (
  workspaceId: string,
  searchTerm: string,
  beforeCreatedAt?: string,
  offset: number = 0,
  limit: number = 40
): Promise<Tables<'chats'>[]> => {
  if (typeof window !== 'undefined') {
    const result = await searchChatsAndMessagesWithSnippets(
      workspaceId,
      searchTerm,
      {
        nameOnly: true
      }
    )

    if (!result.data) {
      throw new Error(result.error?.message || 'Failed to search chats')
    }

    let chats = result.data.map((row) => row.chat)

    if (beforeCreatedAt) {
      chats = chats.filter(
        (chat) => (chat.created_at ?? '') <= beforeCreatedAt
      )
    }

    return chats.slice(offset, offset + limit)
  }

  const client = getDefaultClient()
  let query = client
    .from('chats')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
  // Add search term condition if provided
  if (searchTerm) {
    query = query.ilike('name', `%${searchTerm}%`)
  }
  // Add condition to fetch chats before a specific created_at timestamp
  if (!!beforeCreatedAt) {
    query = query.lte('created_at', beforeCreatedAt)
  }
  // Apply range for pagination
  query = query.range(offset, offset + limit - 1)
  const { data: chats, error } = await query
  if (error) {
    throw new Error(error.message)
  }
  return chats
}
